<script lang="ts">
  import { slide } from 'svelte/transition'
  import { selectedModel, aiServiceState } from '$lib/stores/aiService'
  import { settingsStore, settingsActions } from '$lib/stores/settings'
  import { aiServiceActions } from '$lib/stores/aiService'
  import { getModelById, filterModelsByApiKeys, type AIModel } from '$lib/config/aiModels'
  import { subscriptionStore, isFreeUser, openUpgradePrompt } from '$lib/stores/subscription'
  import { get } from 'svelte/store'
  import { useClickOutside, useCustomEvent } from '$lib/hooks/useEventListener'
  
  // Import provider icons
  import openaiIcon from '$lib/../assets/ai-model-icons/openai.png'
  import claudeIcon from '$lib/../assets/ai-model-icons/claude.png'
  import googleIcon from '$lib/../assets/ai-model-icons/google.png'
  import upstageIcon from '$lib/../assets/ai-model-icons/upstage.png'
  import openrouterIcon from '$lib/../assets/ai-model-icons/openrouter.png'
  
  interface Props {
    isVisible: boolean
    selectedText?: string
    onGenerate: (prompt: string, model: string, mode?: 'sentence' | 'paragraph') => void | Promise<void>
    onCancel: () => void
    errorMessage?: string
    onRetry?: () => void
    onHeightChange?: (height: number) => void
    currentTabId?: string
  }
  
  let {
    isVisible,
    selectedText = '',
    onGenerate,
    onCancel,
    errorMessage,
    onRetry,
    onHeightChange,
    currentTabId = 'default'
  }: Props = $props()
  
  // Track the last tab ID to detect tab changes
  let lastTabId = $state(currentTabId)
  
  // Use sessionStorage to persist prompt input per tab during session only
  // Will be cleared on browser/app restart
  let promptInput = $state('')
  let textareaElement = $state<HTMLTextAreaElement | null>(null)
  let showModelDropdown = $state(false)
  let modelSelectorRef = $state<HTMLDivElement | null>(null)
  let chatContainerRef = $state<HTMLDivElement | null>(null)
  let isInputFocused = $state(false)
  let isGenerating = $state(false)
  let lastGenerateParams = $state<{prompt: string, model: string, mode?: 'sentence' | 'paragraph'} | null>(null)
  
  // Check if dark theme is actually applied to the DOM
  let isDarkTheme = $state(false)
  let isComposing = $state(false) // Track IME composition state
  
  // Track height changes
  $effect(() => {
    if (!chatContainerRef || !onHeightChange) return
    
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.borderBoxSize?.[0]?.blockSize || entry.contentRect.height
        onHeightChange(height)
      }
    })
    
    resizeObserver.observe(chatContainerRef)
    
    return () => {
      resizeObserver.disconnect()
    }
  })
  
  // Global ESC key handler - similar to UnifiedPromptPanel
  $effect(() => {
    if (!isVisible) return
    
    const handleGlobalEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onCancel()
      }
    }
    
    // Use capture phase to intercept before other handlers
    document.addEventListener('keydown', handleGlobalEscape, true)
    
    return () => {
      document.removeEventListener('keydown', handleGlobalEscape, true)
    }
  })
  
  // Watch for theme changes
  $effect(() => {
    if (typeof document === 'undefined') return;
    
    const checkTheme = () => {
      // Check both explicit dark theme class and system preference in auto mode
      const hasExplicitDarkTheme = document.documentElement.classList.contains('dark-theme');
      const isAutoMode = !document.documentElement.classList.contains('light-theme') && !document.documentElement.classList.contains('dark-theme');
      const prefersColorSchemeDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      isDarkTheme = hasExplicitDarkTheme || (isAutoMode && prefersColorSchemeDark);
    }
    
    // Initial check
    checkTheme();
    
    // Watch for class changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    // Watch for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', checkTheme);
    
    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', checkTheme);
    };
  })
  
  const MAX_PROMPT_LENGTH = 200
  
  // Settings and state
  const settings = $derived($settingsStore)
  const isAnalyzing = $derived($aiServiceState.isAnalyzing)
  
  // Get current model from settings store - reactive
  const currentModelId = $derived(settings.ai?.selectedModel || 'openai/gpt-oss-120b')
  
  // Available AI models
  const availableAIModels = $derived(() => {
    const aiProviders = settings.aiProviders || []
    const enabledProviders = aiProviders
      .filter(p => p.enabled && (
        (p.apiKey && p.apiKey.trim() !== '') || 
        p.id === 'openrouter'
      ))
      .map(p => p.id)
    
    return filterModelsByApiKeys(enabledProviders)
  })
  
  // Sort AI models
  const sortedAIModels = $derived(() => {
    const models = availableAIModels()
    const current = currentModelId
    
    return models.sort((a, b) => {
      if (a.id === current) return -1
      if (b.id === current) return 1
      if (a.recommended && !b.recommended) return -1
      if (!a.recommended && b.recommended) return 1
      return a.name.localeCompare(b.name)
    })
  })
  
  const currentAIModel = $derived(getModelById(currentModelId) || null)
  
  // Provider icons map
  const providerIcons: Record<string, string> = {
    openai: openaiIcon,
    anthropic: claudeIcon,
    google: googleIcon,
    upstage: upstageIcon,
    openrouter: openrouterIcon
  }
  
  // Get provider icon
  function getProviderIcon(provider: string): string {
    return providerIcons[provider] || openrouterIcon
  }
  
  // Focus input when visible
  $effect(() => {
    if (isVisible && textareaElement) {
      setTimeout(() => {
        textareaElement?.focus()
        isInputFocused = true
        // Auto-resize to fit content
        adjustTextareaHeight()
      }, 100)
    }
  })
  
  // Handle visibility changes
  $effect(() => {
    if (!isVisible) {
      // Don't clear input when hidden, just unfocus
      isInputFocused = false
    }
  })
  
  // Load prompt when tab changes or component becomes visible
  $effect(() => {
    if (!isVisible) return
    
    // If tab changed, clear the prompt
    if (currentTabId !== lastTabId) {
      promptInput = ''
      lastTabId = currentTabId
      // Clear storage for new tab
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(`generation-prompt-${currentTabId}`)
      }
    } else {
      // Load saved prompt for current tab
      if (typeof window !== 'undefined') {
        const savedPrompt = sessionStorage.getItem(`generation-prompt-${currentTabId}`)
        if (savedPrompt !== null) {
          promptInput = savedPrompt
        }
      }
    }
  })
  
  // Save prompt input to sessionStorage when it changes
  $effect(() => {
    if (typeof window !== 'undefined' && promptInput !== undefined && currentTabId) {
      sessionStorage.setItem(`generation-prompt-${currentTabId}`, promptInput)
    }
  })
  
  // Click outside is now handled in MainEditor.svelte with editor-wrapper onclick
  
  // Handle focus/blur for input
  function handleFocus() {
    isInputFocused = true
  }
  
  function handleBlur() {
    isInputFocused = false
  }
  
  // Clear session storage on component mount if it's a fresh page load
  // This ensures the prompt is cleared on app restart
  $effect(() => {
    // Check if this is a fresh page load (not a hot reload)
    if (typeof window !== 'undefined' && performance.navigation.type === 1) {
      // Type 1 means page was reloaded
      sessionStorage.removeItem('generation-prompt-input')
      promptInput = ''
    }
  })
  
  async function handleSubmit() {
    if (!promptInput.trim() || isGenerating) return
    
    // Detect generation mode from prompt
    const prompt = promptInput.trim()
    let mode: 'sentence' | 'paragraph' | undefined
    
    if (prompt.includes('문장') || prompt.includes('짧게') || prompt.includes('간단')) {
      mode = 'sentence'
    } else if (prompt.includes('문단') || prompt.includes('길게') || prompt.includes('자세')) {
      mode = 'paragraph'
    }
    
    // Use current model ID from settings for generation
    const modelToUse = currentModelId
    
    // Save parameters for retry
    lastGenerateParams = { prompt, model: modelToUse, mode }
    
    try {
      isGenerating = true
      await onGenerate(prompt, modelToUse, mode)
      
      // Clear the prompt after successful generation
      promptInput = ''
      sessionStorage.removeItem('generation-prompt-input')
    } catch (error) {
      console.error('Generation failed:', error)
      // Error will be handled by parent component
    } finally {
      isGenerating = false
    }
  }
  
  async function handleRetry() {
    if (!lastGenerateParams || isGenerating) return
    
    try {
      isGenerating = true
      await onGenerate(lastGenerateParams.prompt, lastGenerateParams.model, lastGenerateParams.mode)
      
      // Clear the prompt after successful generation
      promptInput = ''
      sessionStorage.removeItem('generation-prompt-input')
    } catch (error) {
      console.error('Retry failed:', error)
      // Error will be handled by parent component
    } finally {
      isGenerating = false
    }
  }
  
  function handleKeyDown(e: KeyboardEvent) {
    // Allow standard text editing shortcuts when input is focused
    if (e.metaKey || e.ctrlKey) {
      // Don't prevent default for copy, paste, cut, select all, undo, redo
      if (['c', 'v', 'x', 'a', 'z'].includes(e.key.toLowerCase())) {
        // Don't stop propagation, just let it happen naturally
        return
      }
    }
    
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      onCancel()
    } else if (e.key === 'Enter' && !e.shiftKey) {
      // Don't submit if IME composition is in progress
      if (isComposing) {
        return
      }
      e.preventDefault()
      handleSubmit()
    }
  }
  
  function handleCompositionStart() {
    isComposing = true
  }
  
  function handleCompositionEnd() {
    isComposing = false
  }
  
  function handleInput() {
    adjustTextareaHeight()
  }
  
  function adjustTextareaHeight() {
    if (!textareaElement) return
    textareaElement.style.height = 'auto'
    // Debounce height adjustment to avoid forced reflows
    requestAnimationFrame(() => {
      if (textareaElement) {
        textareaElement.style.height = Math.min(textareaElement.scrollHeight, 120) + 'px'
      }
    })
  }
  
  function toggleModelDropdown(e: MouseEvent) {
    e.stopPropagation() // Prevent immediate close
    if (isAnalyzing) return
    showModelDropdown = !showModelDropdown
  }
  
  function closeModelDropdown() {
    showModelDropdown = false
  }
  
  // Handle click outside for model dropdown
  $effect(() => {
    if (!showModelDropdown) return
    
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as HTMLElement
      if (!modelSelectorRef?.contains(target)) {
        closeModelDropdown()
      }
    }
    
    // Add slight delay to prevent immediate close on open
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
    }, 10)
    
    return () => {
      clearTimeout(timer)
      document.removeEventListener('click', handleClickOutside)
    }
  })
  
  async function selectAIModel(model: AIModel) {
    // Check if free user is trying to select non-OpenRouter model
    if (get(isFreeUser) && model.provider !== 'openrouter') {
      openUpgradePrompt('feature_locked', 'AI 분석 - 프리미엄 모델 선택')
      closeModelDropdown()
      return
    }
    
    // Update global model selection in settings
    settingsActions.updateAISettings({ selectedModel: model.id })
    
    closeModelDropdown()
    // Force re-render by updating a local state
    showModelDropdown = false
  }
</script>

{#if isVisible}
  <div 
    class="generation-bar"
    transition:slide={{ duration: 150, axis: 'y' }}
  >
    <div class="chat-container" bind:this={chatContainerRef}>
      <!-- Main textarea -->
      <textarea
        bind:this={textareaElement}
        bind:value={promptInput}
        class="prompt-input"
        aria-label={selectedText ? "선택한 텍스트 편집 프롬프트" : "AI 생성 프롬프트"}
        placeholder={selectedText ? "이 텍스트를 어떻게 변경할까요?" : "무엇을 생성할까요?"}
        maxlength={MAX_PROMPT_LENGTH}
        onkeydown={handleKeyDown}
        oninput={handleInput}
        onfocus={handleFocus}
        onblur={handleBlur}
        oncompositionstart={handleCompositionStart}
        oncompositionend={handleCompositionEnd}
        rows="1"
      ></textarea>
      
      <!-- Bottom controls -->
      <div class="bottom-controls">
        <!-- Model selector at bottom left -->
        <div class="model-selector" bind:this={modelSelectorRef}>
          <button
            class="model-btn"
            onclick={(e) => toggleModelDropdown(e)}
            disabled={isAnalyzing}
            title="AI 모델 선택"
            aria-label="AI 모델 선택"
            aria-expanded={showModelDropdown}
          >
            {#key currentModelId}
              {#if currentAIModel}
                <img 
                  src={getProviderIcon(currentAIModel.provider)} 
                  alt={currentAIModel.provider} 
                  class="provider-icon"
                  style="filter: {isDarkTheme ? 'brightness(0) invert(1)' : 'none'}"
                />
              {:else}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 2v20M2 12h20"/>
                </svg>
              {/if}
              <span class="model-name">{currentAIModel?.name || 'AI 모델'}</span>
            {/key}
            <svg class="chevron-icon" class:rotated={showModelDropdown} width="6" height="6" viewBox="0 0 24 24">
              <path d="M19 9l-7 7-7-7" stroke="currentColor" fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          
          <!-- Model dropdown -->
          {#if showModelDropdown}
            <div class="model-dropdown">
              <div class="model-list">
                {#each sortedAIModels() as model}
                  <button
                    class="model-item"
                    class:selected={currentModelId === model.id}
                    onclick={() => selectAIModel(model)}
                  >
                    <img 
                      src={getProviderIcon(model.provider)} 
                      alt={model.provider} 
                      class="item-provider-icon"
                      style="filter: {isDarkTheme ? 'brightness(0) invert(1)' : 'none'}"
                    />
                    <div class="item-info">
                      <div class="item-name">
                        {model.name}
                        {#if model.recommended}
                          <span class="recommended-badge">추천</span>
                        {/if}
                      </div>
                      <div class="item-provider">{model.provider}</div>
                    </div>
                    {#if currentModelId === model.id}
                      <svg class="check-icon" width="12" height="12" viewBox="0 0 14 14">
                        <path 
                          d="M2 7l3 3 7-7" 
                          stroke="#6366f1" 
                          fill="none" 
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    {/if}
                  </button>
                {/each}
              </div>
            </div>
          {/if}
        </div>
        
        <!-- Right side controls -->
        <div class="right-controls">
          <!-- Character count next to send button -->
          {#if promptInput.length >= 100}
            <span class="char-count">{promptInput.length}/{MAX_PROMPT_LENGTH}</span>
          {/if}
          
          <!-- Send button -->
          <button
            class="send-btn"
            onclick={handleSubmit}
            disabled={!promptInput.trim()}
            title="생성 (Enter)"
            aria-label="생성"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .generation-bar {
    padding: 12px;
    background: transparent;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
  }
  
  .chat-container {
    position: relative;
    background: var(--bg-primary, white);
    border: 1px solid var(--border-primary, #e5e7eb);
    border-radius: 12px;
    padding: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.2s;
    box-sizing: border-box;
    width: 100%;
  }
  
  .chat-container:focus-within {
    border-color: var(--border-secondary, #d1d5db);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
  
  /* Character count */
  .char-count {
    font-size: 11px;
    color: var(--text-tertiary, #9ca3af);
    font-family: 'SF Mono', Monaco, monospace;
    opacity: 0.7;
    margin-right: 8px;
  }
  
  /* Main textarea */
  .prompt-input {
    width: 100%;
    box-sizing: border-box;
    padding: 4px 0;
    padding-bottom: 36px;
    font-size: 14px;
    line-height: 1.5;
    border: none;
    background: transparent;
    color: var(--text-primary, #1a1a1a);
    outline: none;
    resize: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    min-height: 24px;
    max-height: 100px;
    overflow-y: auto;
  }
  
  .prompt-input::placeholder {
    color: var(--text-tertiary, #9ca3af);
  }
  
  /* Custom scrollbar */
  .prompt-input::-webkit-scrollbar {
    width: 6px;
  }
  
  .prompt-input::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .prompt-input::-webkit-scrollbar-thumb {
    background: var(--border-primary, #e5e7eb);
    border-radius: 3px;
  }
  
  /* Bottom controls */
  .bottom-controls {
    position: absolute;
    bottom: 8px;
    left: 12px;
    right: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  /* Right side controls */
  .right-controls {
    display: flex;
    align-items: center;
  }
  
  /* Model selector */
  .model-selector {
    position: relative;
  }
  
  .provider-icon {
    width: 12px;
    height: 12px;
    object-fit: contain;
  }
  
  .model-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 3px 6px;
    background: var(--bg-secondary, #f9fafb);
    border: 1px solid var(--border-primary, #e5e7eb);
    border-radius: 6px;
    font-size: 10px;
    color: var(--text-primary, #374151);
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }
  
  .model-btn:hover:not(:disabled) {
    background: var(--bg-tertiary, #f3f4f6);
    border-color: var(--border-secondary, #d1d5db);
  }
  
  .model-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .model-name {
    font-weight: 450;
    font-size: 10px;
    white-space: nowrap;
  }
  
  .chevron-icon {
    transition: transform 0.2s;
    opacity: 0.6;
    margin-left: -1px;
    color: var(--text-secondary);
  }
  
  .chevron-icon.rotated {
    transform: rotate(180deg);
  }
  
  /* Model dropdown */
  .model-dropdown {
    position: absolute;
    bottom: calc(100% + 6px);
    left: 0;
    min-width: 240px;
    background: var(--bg-primary, white);
    border: 1px solid var(--border-primary, #e5e7eb);
    border-radius: 8px;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
    z-index: 2100;
    overflow: hidden;
    width: max-content;
    max-width: 320px;
  }
  
  .model-list {
    padding: 6px;
    max-height: 240px;
    overflow: hidden;
    overflow-y: auto;
    scrollbar-width: none; /* Firefox */
  }
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  .model-list::-webkit-scrollbar {
    display: none;
  }
  
  .model-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    width: 100%;
    padding: 6px 8px;
    border: none;
    background: transparent;
    border-radius: 5px;
    font-size: 11px;
    color: var(--text-primary, #374151);
    cursor: pointer;
    transition: all 0.1s;
    text-align: left;
  }
  
  .model-item:hover {
    background: var(--bg-secondary, #f9fafb);
  }
  
  .model-item.selected {
    background: rgba(99, 102, 241, 0.1);
  }
  
  .item-provider-icon {
    width: 14px;
    height: 14px;
    object-fit: contain;
    flex-shrink: 0;
  }
  
  .item-info {
    flex: 1;
    min-width: 0;
  }
  
  .item-name {
    font-weight: 450;
    line-height: 1.2;
    font-size: 11px;
  }
  
  .item-provider {
    font-size: 9px;
    color: var(--text-tertiary, #9ca3af);
    line-height: 1.2;
    margin-top: 1px;
  }
  
  .recommended-badge {
    display: inline-block;
    padding: 1px 3px;
    margin-left: 3px;
    background: #10b981;
    color: white;
    font-size: 8px;
    font-weight: 600;
    border-radius: 2px;
    vertical-align: middle;
  }
  
  .check-icon {
    flex-shrink: 0;
  }
  
  /* Send button */
  .send-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    background: var(--accent-primary, #6366f1);
    border: none;
    border-radius: 6px;
    color: white;
    cursor: pointer;
    transition: all 0.15s;
  }
  
  .send-btn:hover:not(:disabled) {
    background: var(--accent-secondary, #5558e3);
    transform: scale(1.05);
  }
  
  .send-btn:disabled {
    background: var(--border-primary, #e5e7eb);
    color: var(--text-tertiary, #9ca3af);
    cursor: not-allowed;
  }
  
  /* Remove media query dark mode as we use CSS variables now */

  /* Dark theme adjustments */
  :global(:root.dark-theme) .chat-container:focus-within {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  }

  :global(:root.dark-theme) .prompt-input::-webkit-scrollbar-thumb {
    background: var(--border-secondary);
  }

  :global(:root.dark-theme) .model-dropdown {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
  }

  :global(:root.dark-theme) .model-item:hover {
    background: var(--bg-tertiary);
  }

  :global(:root.dark-theme) .model-item.selected {
    background: rgba(124, 122, 255, 0.15);
  }
  
  :global(:root.dark-theme) .send-btn:disabled {
    background: var(--bg-tertiary);
    color: var(--text-tertiary);
  }
  
  /* Apply filter to icons in dark mode */
  :global(:root.dark-theme) .chevron-icon {
    filter: invert(1);
    opacity: 0.7;
  }
  
  :global(:root.dark-theme) .chevron-icon.rotated {
    filter: invert(1);
    opacity: 0.7;
  }
  
  /* Provider icon filters are now handled inline in the template */
  
  :global(:root.dark-theme) .model-btn svg:not(.chevron-icon) {
    filter: brightness(1.5);
  }
</style>
